import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import {
  Tab,
  TabList,
  Button,
  makeStyles,
  shorthands,
} from "@fluentui/react-components";
import {
  DocumentRegular,
  DocumentFilled,
  PersonRegular,
  PersonFilled,
  VehicleCarRegular,
  VehicleCarFilled,
  ShieldRegular,
  ShieldFilled,
  ArrowLeftFilled,
  ArrowRightRegular,
  ArrowLeftRegular,
  ErrorCircleRegular,
} from "@fluentui/react-icons";

import { ROUTES } from "library/constants";
import { useItemPageStore, initialItemData } from "store/ItemPageStore";

// Import section components
import ContractSection from "./sections/ContractSection";
import ClientSection from "./sections/ClientSection";
import VehicleSection from "./sections/VehicleSection";
import CoveragesSection from "./sections/CoveragesSection";

// Import validation error components
import ValidationErrorSidebar, {
  ValidationError,
} from "./components/ValidationErrorSidebar";
import {
  extractValidationErrors,
  TAB_CONFIG,
} from "./services/validationService";
import {
  useItemPageValidationSchema,
  useYupValidationResolver,
  validateTab,
  type TabName,
} from "./useFormValidationSchema";

const useStyles = makeStyles({
  card: {
    ...shorthands.margin("auto"),
    width: "100%",
    maxWidth: "1280px",
    marginTop: "20px",
    justifyContent: "center",
    overflow: "auto",
  },
  navbar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "8px 16px",
    backgroundColor: "#f5f5f5",
  },
  wizardFooter: {
    position: "fixed",
    bottom: 0,
    left: 0,
    right: 0,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "16px",
    backgroundColor: "tokens.colorNeutralBackground1",
    borderTop: `1px solid tokens.colorNeutralStroke1`,
    boxShadow: "tokens.shadow16",
    zIndex: 999, // Below the floating total (1000)
  },
  wizardFooterContent: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    maxWidth: "1280px",
    margin: "0 auto",
  },
  navigationButtons: {
    display: "flex",
    gap: "8px",
  },
  disabledTab: {
    opacity: 0.5,
    pointerEvents: "none",
  },
  pageContainer: {
    paddingBottom: "80px", // Add padding to prevent content from being hidden behind fixed footer
  },
  errorButton: {
    position: "fixed",
    top: "50%",
    right: "16px",
    transform: "translateY(-50%)",
    zIndex: 1001, // Above the floating total
    backgroundColor: "#d13438", // Red background
    border: "1px solid #d13438", // Red border
    color: "#ffffff", // White text
    ":hover": {
      backgroundColor: "#b92b2f", // Darker red on hover
    },
    ":active": {
      backgroundColor: "#a02328", // Even darker red when active
    },
  },
});

const WIZARD_STEPS = [
  { key: "contract" },
  { key: "client" },
  { key: "vehicle" },
  { key: "coverages" },
];

const ItemPage = () => {
  const { t } = useTranslation();
  const classes = useStyles();
  const { itemId } = useParams();
  const navigate = useNavigate();

  // Use the store only for tab navigation and loading state
  const [{ selectedTab, isLoading }, { setSelectedTab, reset }] =
    useItemPageStore();

  // Track completed steps
  const [completedSteps, setCompletedSteps] = useState<Set<string>>(new Set());

  // Track coverages section form changes for submit button state
  const [hasCoveragesFormChanges, setHasCoveragesFormChanges] = useState(false);

  // Validation error sidebar state
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>(
    []
  );
  const [isErrorSidebarOpen, setIsErrorSidebarOpen] = useState(false);

  const schema = useItemPageValidationSchema(yup, t);
  const resolver = useYupValidationResolver(schema);

  // Form is the single source of truth for data
  const form = useForm({
    resolver,
    defaultValues: initialItemData,
    mode: "onChange", // Enable onChange validation for wizard
  });

  const currentStepIndex = WIZARD_STEPS.findIndex(
    (step) => step.key === selectedTab
  );

  // Watch for form validation state changes and update validation errors
  // useEffect(() => {
  //   const errors = form.formState.errors;

  //   // Only update validation errors if there are actual errors
  //   if (Object.keys(errors).length > 0) {
  //     // convert errors to validation error format
  //     // from:
  //     // {
  //     //     "contractStartDate": {
  //     //         "type": "nullable",
  //     //         "message": "This field is required"
  //     //     }
  //     // }
  //     // To ValidationError[]
  //     // [
  //     //     {
  //     //         "fieldPath": "contractStartDate",
  //     //         "message": "This field is required",
  //     //         "tab": "contract",
  //     //         "tabIndex": 0
  //     //     }
  //     // ]
  //     const currentSectionErrors = extractValidationErrors(
  //       errors,
  //       (key: string, fallback?: string) => {
  //         return t(key, fallback || key);
  //       }
  //     );

  //     // Update validation errors state
  //     setValidationErrors(currentSectionErrors);
  //   }
  //   // Remove the else block that was clearing errors
  //   // Let the validation errors be cleared only by user actions (handleNext, handleTabClick, etc.)
  // }, [form.formState.errors, t]);

  // Separate effect to handle sidebar closing when no errors
  useEffect(() => {
    if (validationErrors.length === 0 && isErrorSidebarOpen) {
      setIsErrorSidebarOpen(false);
    }
  }, [validationErrors.length, isErrorSidebarOpen]);

  // Update validateCurrentSection to use form data instead of store data
  const validateCurrentSection = async () => {
    // Get the current tab name
    const currentTabName = selectedTab as TabName;

    // Get current form values
    const formData = form.getValues();

    // Use the new tab-based validation with form data
    const { isValid, errors } = await validateTab(currentTabName, formData, t);

    // Flatten the nested error structure to match TAB_CONFIG field paths
    const flattenErrors = (obj: any, prefix = ""): any => {
      const flattened: any = {};

      Object.keys(obj).forEach((key) => {
        const value = obj[key];
        const newKey = prefix ? `${prefix}.${key}` : key;

        if (
          value &&
          typeof value === "object" &&
          !Array.isArray(value) &&
          !value.message
        ) {
          // Recursively flatten nested objects
          Object.assign(flattened, flattenErrors(value, newKey));
        } else {
          // This is an actual error value
          flattened[newKey] = value;
        }
      });

      return flattened;
    };

    const flattenedErrors = flattenErrors(errors);

    return { isValid, errors: flattenedErrors };
  };
  // Handle error click in sidebar
  const handleErrorClick = (error: ValidationError) => {
    // Navigate to the tab containing the error
    const tabKeys = WIZARD_STEPS.map((step) => step.key);
    const targetTabKey = tabKeys[error.tabIndex];
    if (targetTabKey && isTabEnabled(targetTabKey)) {
      setSelectedTab(targetTabKey);
    }

    // Close the sidebar
    setIsErrorSidebarOpen(false);

    // Focus the field using the original field path
    setTimeout(() => {
      // Try different selectors to find the field
      let fieldElement = document.querySelector(`[name="${error.fieldPath}"]`);

      // If not found, try with nested field path (for Address.City etc.)
      if (!fieldElement && error.fieldPath.includes(".")) {
        const lastPart = error.fieldPath.split(".").pop();
        fieldElement = document.querySelector(`[name="${lastPart}"]`);
      }

      if (fieldElement) {
        (fieldElement as HTMLElement).focus();
        fieldElement.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 100);
  }; // Handle next button click
  const handleNext = async () => {
    const { isValid, errors } = await validateCurrentSection();

    if (isValid) {
      // Clear any previous validation errors since validation passed
      setValidationErrors([]);
      setIsErrorSidebarOpen(false);

      // Mark current step as completed
      setCompletedSteps((prev) => new Set([...Array.from(prev), selectedTab]));

      // Move to next step
      if (currentStepIndex < WIZARD_STEPS.length - 1) {
        const nextStep = WIZARD_STEPS[currentStepIndex + 1];
        if (nextStep) {
          setSelectedTab(nextStep.key);
        }
      }
    } else {
      // Extract validation errors directly from the flattened errors
      const currentSectionErrors = extractValidationErrors(
        errors,
        (key: string, fallback?: string) => {
          return t(key, fallback || key);
        }
      );

      setValidationErrors(currentSectionErrors);

      // Show validation errors sidebar
      setIsErrorSidebarOpen(true);
    }
  }; // Handle previous button click
  const handlePrevious = () => {
    if (currentStepIndex > 0) {
      const prevStep = WIZARD_STEPS[currentStepIndex - 1];
      if (prevStep) {
        setSelectedTab(prevStep.key);

        // Clear validation errors when going back to avoid showing stale errors
        if (validationErrors.length > 0) {
          setValidationErrors([]);
          setIsErrorSidebarOpen(false);
        }
      }
    }
  };
  // Handle tab click
  const handleTabClick = (tabKey: string) => {
    const tabIndex = WIZARD_STEPS.findIndex((step) => step.key === tabKey);

    // Allow navigation to completed tabs or the next available tab
    const prevStep = WIZARD_STEPS[tabIndex - 1];
    if (tabIndex === 0 || (prevStep && completedSteps.has(prevStep.key))) {
      setSelectedTab(tabKey);

      // Clear validation errors when switching tabs to avoid showing stale errors
      if (validationErrors.length > 0) {
        setValidationErrors([]);
        setIsErrorSidebarOpen(false);
      }
    }
  };

  // Check if a tab should be enabled
  const isTabEnabled = (tabKey: string) => {
    const tabIndex = WIZARD_STEPS.findIndex((step) => step.key === tabKey);
    const prevStep = WIZARD_STEPS[tabIndex - 1];
    return tabIndex === 0 || (prevStep && completedSteps.has(prevStep.key));
  };
  // Handle form submission
  const handleSubmit = async () => {
    // Get the final form data
    const formData = form.getValues();

    // TODO: Implement form submission with formData
    console.log("Submitting form data:", formData);

    // Example API call:
    // try {
    //   await submitItemData(formData);
    //   navigate(ROUTES.SUCCESS);
    // } catch (error) {
    //   console.error("Submission error:", error);
    // }
  };

  // Load item data if itemId is present
  useEffect(() => {
    if (itemId) {
      // TODO: Load item data from API
      // const data = await loadItemData(itemId);
      // form.reset(data);
    }
  }, [itemId, form]);

  const isLastStep = currentStepIndex === WIZARD_STEPS.length - 1;
  const isFirstStep = currentStepIndex === 0;

  return (
    <div className={classes.pageContainer}>
      <div className={classes.navbar}>
        <div>
          <Button
            appearance="subtle"
            icon={<ArrowLeftFilled />}
            onClick={() => {
              navigate(ROUTES.HOME);
            }}
          >
            {t("ila.back.label")}
          </Button>
        </div>
        <div style={{ marginLeft: "auto" }}>
          {/* Additional buttons can go here */}
        </div>
      </div>
      <TabList selectedValue={selectedTab} className={classes.card}>
        <Tab
          icon={
            selectedTab === "contract" ? (
              <DocumentFilled />
            ) : (
              <DocumentRegular />
            )
          }
          value="contract"
          onClick={() => handleTabClick("contract")}
          className={!isTabEnabled("contract") ? classes.disabledTab : ""}
        >
          {t("ila.contractInfoSection.label")}
        </Tab>
        <Tab
          icon={selectedTab === "client" ? <PersonFilled /> : <PersonRegular />}
          value="client"
          onClick={() => handleTabClick("client")}
          className={!isTabEnabled("client") ? classes.disabledTab : ""}
        >
          {t("ila.clientInfoSection.label")}
        </Tab>
        <Tab
          icon={
            selectedTab === "vehicle" ? (
              <VehicleCarFilled />
            ) : (
              <VehicleCarRegular />
            )
          }
          value="vehicle"
          onClick={() => handleTabClick("vehicle")}
          className={!isTabEnabled("vehicle") ? classes.disabledTab : ""}
        >
          {t("ila.vehicleDetailsSection.label")}
        </Tab>
        <Tab
          icon={
            selectedTab === "coverages" ? <ShieldFilled /> : <ShieldRegular />
          }
          value="coverages"
          onClick={() => handleTabClick("coverages")}
          className={!isTabEnabled("coverages") ? classes.disabledTab : ""}
        >
          {t("ila.coveragesSection.label")}
        </Tab>
      </TabList>
      <>
        {selectedTab === "contract" ? (
          <ContractSection form={form} isLoading={isLoading} />
        ) : null}
        {selectedTab === "client" ? (
          <ClientSection form={form} isLoading={isLoading} />
        ) : null}
        {selectedTab === "vehicle" ? (
          <VehicleSection form={form} isLoading={isLoading} />
        ) : null}{" "}
        {selectedTab === "coverages" ? (
          <CoveragesSection
            form={form}
            isLoading={isLoading}
            onFormChangesUpdate={setHasCoveragesFormChanges}
          />
        ) : null}
      </>
      {/* Validation Error Sidebar */}
      <ValidationErrorSidebar
        isOpen={isErrorSidebarOpen}
        onClose={() => setIsErrorSidebarOpen(false)}
        errors={validationErrors}
        onErrorClick={handleErrorClick}
      />
      {/* Floating Error Button */}
      {validationErrors.length > 0 && (
        <Button
          appearance="primary"
          icon={<ErrorCircleRegular />}
          className={classes.errorButton}
          onClick={() => setIsErrorSidebarOpen(true)}
          size="large"
          style={{
            backgroundColor: "#d13438",
            borderColor: "#d13438",
            color: "#ffffff",
          }}
        >
          {validationErrors.length}{" "}
          {validationErrors.length === 1
            ? t("ila.validation.error")
            : t("ila.validation.errors")}
        </Button>
      )}
      {/* Fixed Wizard Navigation Footer */}
      <div className={classes.wizardFooter}>
        <div className={classes.wizardFooterContent}>
          <div className={classes.navigationButtons}>
            <Button
              appearance="secondary"
              icon={<ArrowLeftRegular />}
              iconPosition="before"
              onClick={handlePrevious}
              disabled={isFirstStep || isLoading}
            >
              {t("ila.previous")}
            </Button>
          </div>

          <div className={classes.navigationButtons}>
            {!isLastStep && (
              <Button
                appearance="primary"
                icon={<ArrowRightRegular />}
                iconPosition="after"
                onClick={handleNext}
                disabled={isLoading}
              >
                {t("ila.next")}
              </Button>
            )}{" "}
            {isLastStep && (
              <Button
                appearance="primary"
                onClick={handleSubmit}
                disabled={
                  isLoading ||
                  (selectedTab === "coverages" && hasCoveragesFormChanges)
                }
                title={
                  hasCoveragesFormChanges && selectedTab === "coverages"
                    ? "Please update premium calculation before submitting"
                    : undefined
                }
              >
                {isLoading
                  ? "Submitting..."
                  : hasCoveragesFormChanges && selectedTab === "coverages"
                  ? "Update Premium First"
                  : t("ila.submit")}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ItemPage;
